/** Firebase stores an in-progress signInWithRedirect flag in sessionStorage. */
export function hasPendingFirebaseRedirect(storage: {
  length: number;
  key: (index: number) => string | null;
}): boolean {
  for (let i = 0; i < storage.length; i++) {
    const key = storage.key(i) ?? '';
    if (key.includes('pendingRedirect')) return true;
  }
  return false;
}

export function shouldSkipOAuthRedirectCompletion(
  env: Record<string, string | undefined> = process.env as Record<string, string | undefined>,
): boolean {
  return Boolean(env.NEXT_PUBLIC_E2E_MOCK_UID) || env.NEXT_PUBLIC_TOUR_BUILD === 'true';
}

/** Keep /login mounted until getRedirectResult finishes, otherwise the bounce drops the credential. */
export function shouldBounceLoginRoute(opts: {
  pathname: string;
  oauthSettled: boolean;
  hasOAuthCredential: boolean;
}): boolean {
  const login = opts.pathname === '/login' || opts.pathname === '/login/';
  if (!login) return false;
  if (!opts.oauthSettled) return false;
  if (opts.hasOAuthCredential) return false;
  return true;
}

export function oauthRedirectFailureMessage(
  hadPending: boolean,
  cred: unknown,
  currentUser?: unknown | null,
): string | null {
  if (cred || currentUser) return null;
  if (!hadPending) return null;
  return 'Google sign-in did not complete. Try again.';
}

/** Account menu only after session bootstrap; a Firebase user alone must not hide login errors. */
export function navShowsAccountMenu(opts: {
  sessionReady: boolean;
  user: { uid?: string } | null;
  profile: { id?: string } | null;
  joinGate: boolean;
}): boolean {
  if (!opts.sessionReady || !opts.user?.uid) return false;
  if (opts.joinGate) return true;
  return Boolean(opts.profile?.id);
}

type RedirectResultFn<T> = () => Promise<T>;

let inflight: Promise<unknown> | null = null;

/** getRedirectResult is one-shot; remounts must reuse the same promise. */
export function completeRedirectResultOnce<T>(fn: RedirectResultFn<T>): Promise<T> {
  if (!inflight) {
    inflight = fn();
  }
  return inflight as Promise<T>;
}

export function resetRedirectResultOnceForTests(): void {
  inflight = null;
}
